/**
 * CharacterSystem.ts
 * Implements the complete Terra Anima Character Creation, 6 Races, 7 Stats,
 * HP-Proportional Movement Speed, 19 Self-Taught Skills (0.0~100.0%),
 * DAoC Triangular Spec Points (1~50 + 10 Enchanter Cap = 60),
 * Multi-Skill Component Crafting, and Enchanter NPC System.
 */

import {
  EntityState,
  EntityRole,
  RaceId,
  StatBlock,
  SelfSkillBlock,
  CraftedComponent,
  CraftedEquipment,
  EnchantBonus,
  CharacterAppearance,
  GenderType,
  BodyShapeType,
  EyeAngleType,
  EyeSizeType,
  HairStyleId,
  FacialFeatureId,
  InventoryItem,
  INVENTORY_CAPACITY,
  INVENTORY_BASE_UNLOCKED,
  INVENTORY_UNLOCK_STEP,
  INVENTORY_UNLOCK_DIAMOND_COST,
} from '../../core/Types';
import { EventBus } from '../../core/EventBus';
import { Currency } from '../../core/Currency';

export const SKIN_COLOR_PALETTE: Array<{ name: string; hex: string }> = [
  { name: '밀색 (표준)', hex: '#fde68a' },
  { name: '창백한 상아', hex: '#fef3c7' },
  { name: '복숭아빛', hex: '#fecdd3' },
  { name: '구릿빛 사막', hex: '#d97706' },
  { name: '짙은 흑단', hex: '#78350f' },
  { name: '성역 은백색', hex: '#e0e7ff' },
  { name: '숲의 청록빛', hex: '#a7f3d0' },
  { name: '안개 잿빛', hex: '#cbd5e1' },
];

export const HAIR_COLOR_PALETTE: Array<{ name: string; hex: string }> = [
  { name: '칠흑색', hex: '#1e293b' },
  { name: '황금빛 금발', hex: '#facc15' },
  { name: '불꽃 적발', hex: '#dc2626' },
  { name: '은백발', hex: '#f8fafc' },
  { name: '밤나무 갈색', hex: '#78350f' },
  { name: '비전 보라', hex: '#9333ea' },
  { name: '빙하 청색', hex: '#2563eb' },
  { name: '신록 에메랄드', hex: '#10b981' },
];

export const ARMOR_COLOR_PALETTE: Array<{ name: string; hex: string }> = [
  { name: '성기사 로얄블루', hex: '#2563eb' },
  { name: '태양 황금빛', hex: '#eab308' },
  { name: '레인저 포레스트', hex: '#15803d' },
  { name: '대마법사 인디고', hex: '#4f46e5' },
  { name: '성녀 순백색', hex: '#f8fafc' },
  { name: '음유시인 크림슨', hex: '#db2777' },
  { name: '수도승 황토갈색', hex: '#854d0e' },
  { name: '흑기사 오닉스', hex: '#1e293b' },
  { name: '블러드 루비', hex: '#b91c1c' },
  { name: '자수정 퍼플', hex: '#7e22ce' },
];

export const MALE_HAIR_STYLES: Array<{ id: HairStyleId; name: string }> = [
  { id: 'short', name: '단정한 숏컷' },
  { id: 'mohawk', name: '북방 모히칸' },
  { id: 'tonsure', name: '삭발 / 민머리' },
  { id: 'braids', name: '전사 땋은머리' },
  { id: 'pixie', name: '바람결 댄디컷' },
];

export const FEMALE_HAIR_STYLES: Array<{ id: HairStyleId; name: string }> = [
  { id: 'long', name: '찰랑이는 긴 생머리' },
  { id: 'ponytail', name: '포니테일 (말총머리)' },
  { id: 'twin_tail', name: '양갈래 트윈테일' },
  { id: 'bob_cut', name: '단아한 보브 단발' },
  { id: 'wavy_long', name: '풍성한 웨이브 롱헤어' },
  { id: 'braids', name: '정교한 삼단 땋은머리' },
];

export const EYE_ANGLE_OPTIONS: Array<{ id: EyeAngleType; name: string }> = [
  { id: 'normal', name: '표준형 눈매' },
  { id: 'sharp', name: '날카로운 상승 눈매' },
  { id: 'droopy', name: '순하고 차분한 하강 눈매' },
];

export const EYE_SIZE_OPTIONS: Array<{ id: EyeSizeType; name: string }> = [
  { id: 'small', name: '작고 날렵한 눈' },
  { id: 'medium', name: '보통 눈' },
  { id: 'large', name: '크고 맑은 눈' },
];

export const BODY_SHAPE_OPTIONS: Array<{ id: BodyShapeType; name: string }> = [
  { id: 'slim', name: '슬림형 (날렵한 체형)' },
  { id: 'normal', name: '표준형 (균형잡힌 체형)' },
  { id: 'muscular', name: '근육형 (단단한 전사 체형)' },
  { id: 'heavy', name: '중후형 (건장한 떡대 체형)' },
];

export const HAIR_STYLE_OPTIONS: Array<{ id: HairStyleId; name: string }> = [
  ...MALE_HAIR_STYLES,
  ...FEMALE_HAIR_STYLES,
];

export const FACIAL_FEATURE_OPTIONS: Array<{ id: FacialFeatureId; name: string }> = [
  { id: 'none', name: '특징 없음' },
  { id: 'beard', name: '풍성한 턱수염' },
  { id: 'mustache', name: '멋진 콧수염' },
  { id: 'scar', name: '역전의 흉터' },
  { id: 'tattoo', name: '고대 문신' },
];

export const TITLE_PREFIX_OPTIONS: string[] = [
  '솔라리스 초보자길드',
  '태양의 기사',
  '초보 모험가',
  '숲의 그림자',
  '비전 탐구자',
  '성역의 사제',
  '방랑 음유시인',
  '무념의 구도자',
  '전설의 장인',
];

export function getDefaultAppearanceForRole(role: EntityRole): CharacterAppearance {
  return {
    gender: 'male',
    skinColor: '#fde68a',
    hairStyle: 'short',
    hairColor: '#78350f',
    facialFeature: 'none',
    eyeColor: '#0f172a',
    eyeAngle: 'normal',
    eyeSize: 'medium',
    bodyShape: 'normal',
    primaryArmorColor: '#b45309', // 기본 황토색 (Undyed Ocher)
    secondaryArmorColor: '#92400e',
    capeColor: '#78350f',
    showHelm: false,
    bodyScale: 1.0,
    titlePrefix: '솔라리스 초보자길드',
    guild: '솔라리스 초보자길드',
  };
}

export function randomizeCharacterAppearance(gender: GenderType = 'male'): CharacterAppearance {
  const pick = <T>(arr: T[]): T => arr[Math.floor(Math.random() * arr.length)];
  const hairList = gender === 'female' ? FEMALE_HAIR_STYLES : MALE_HAIR_STYLES;
  const facial = gender === 'male' ? pick(FACIAL_FEATURE_OPTIONS).id : 'none';
  const scales = [0.92, 0.96, 1.0, 1.04, 1.08];

  return {
    gender,
    skinColor: pick(SKIN_COLOR_PALETTE).hex,
    hairStyle: pick(hairList).id,
    hairColor: pick(HAIR_COLOR_PALETTE).hex,
    facialFeature: facial,
    eyeColor: pick(HAIR_COLOR_PALETTE).hex,
    eyeAngle: pick(EYE_ANGLE_OPTIONS).id,
    eyeSize: pick(EYE_SIZE_OPTIONS).id,
    bodyShape: pick(BODY_SHAPE_OPTIONS).id,
    primaryArmorColor: '#b45309', // 황토색 속옷 고정
    secondaryArmorColor: '#92400e',
    capeColor: '#78350f',
    showHelm: false,
    bodyScale: pick(scales),
    titlePrefix: '솔라리스 초보자길드',
    guild: '솔라리스 초보자길드',
  };
}

export interface RaceDefinition {
  id: RaceId;
  nameKr: string;
  nameEn: string;
  originZone: string;
  description: string;
  baseStats: StatBlock; // Sum = 420
  allowedRoles: EntityRole[];
  lifeAffinities: Array<keyof SelfSkillBlock>;
}

export const TERRA_ANIMA_RACES: Record<RaceId, RaceDefinition> = {
  solarian: {
    id: 'solarian',
    nameKr: '솔라리안 (Solarian)',
    nameEn: 'Solarian',
    originZone: 'Zone 1 솔라리아 구릉지 / Zone 2 그란델 평원',
    description: '솔라리스 왕국의 중심을 이루는 표준 인간족. 모든 능력치가 균형 잡혀 있으며 유일하게 6대 전 직업을 선택할 수 있습니다.',
    baseStats: { str: 60, con: 60, dex: 60, qui: 60, int: 60, pie: 60, cha: 60 },
    allowedRoles: ['tank', 'ranged_dps', 'caster_dps', 'healer', 'buffer', 'monk_hybrid'],
    lifeAffinities: ['farming', 'husbandry', 'rhetoric'],
  },
  borean: {
    id: 'borean',
    nameKr: '보레안 (Borean)',
    nameEn: 'Borean',
    originZone: 'Zone 4 보레아스 설원',
    description: '만년설 산맥의 거대한 북방 전사족. 압도적인 힘(75)과 체력(75), 굳건한 신앙(65)을 타고난 최고의 중장갑 체질입니다.',
    baseStats: { str: 75, con: 75, dex: 50, qui: 55, int: 45, pie: 65, cha: 55 },
    allowedRoles: ['tank', 'healer', 'monk_hybrid'],
    lifeAffinities: ['metalworking', 'woodworking', 'training'],
  },
  sylvanian: {
    id: 'sylvanian',
    nameKr: '실바니안 (Sylvanian)',
    nameEn: 'Sylvanian',
    originZone: 'Zone 3 실바나 대삼림',
    description: '태고의 숲과 교감하는 날렵한 삼림 아인종. 최고의 민첩(75)과 속도(70), 아름다운 목소리(매력 70)를 지녔습니다.',
    baseStats: { str: 45, con: 50, dex: 75, qui: 70, int: 60, pie: 50, cha: 70 },
    allowedRoles: ['ranged_dps', 'caster_dps', 'buffer'],
    lifeAffinities: ['woodworking', 'leatherworking', 'performance'],
  },
  ignian: {
    id: 'ignian',
    nameKr: '이그니안 (Ignian)',
    nameEn: 'Ignian',
    originZone: 'Zone 6 이그니스 황무지',
    description: '열사의 사막을 누비는 유목·무투 민족. 번개 같은 속도(75)와 민첩성(70)으로 회피탱커와 속공 무도가에 특화되어 있습니다.',
    baseStats: { str: 60, con: 55, dex: 70, qui: 75, int: 55, pie: 55, cha: 50 },
    allowedRoles: ['tank', 'ranged_dps', 'buffer', 'monk_hybrid'],
    lifeAffinities: ['leatherworking', 'alchemy', 'stealing'],
  },
  lumian: {
    id: 'lumian',
    nameKr: '루미안 (Lumian)',
    nameEn: 'Lumian',
    originZone: 'Zone 7 루미나 성역섬',
    description: '고대 수정 성역의 마력을 이어받은 귀족 혈통. 타의 추종을 불허하는 지능(80)과 신앙(70)으로 극마력 캐스터에 최적입니다.',
    baseStats: { str: 45, con: 45, dex: 55, qui: 60, int: 80, pie: 70, cha: 65 },
    allowedRoles: ['tank', 'caster_dps', 'healer'],
    lifeAffinities: ['tailoring', 'alchemy', 'experiment'],
  },
  nebulan: {
    id: 'nebulan',
    nameKr: '네뷸란 (Nebulan)',
    nameEn: 'Nebulan',
    originZone: 'Zone 5 네뷸라 늪지',
    description: '안개 늪지의 고대 은둔 구도자 종족. 작지만 단단한 체력(65)과 민첩(65), 지능(65), 신앙(65)이 조화된 하이브리드 종족입니다.',
    baseStats: { str: 55, con: 65, dex: 65, qui: 55, int: 65, pie: 65, cha: 50 },
    allowedRoles: ['ranged_dps', 'caster_dps', 'healer', 'monk_hybrid'],
    lifeAffinities: ['alchemy', 'cooking', 'prayer'],
  },
};

export interface SelfSkillMeta {
  key: keyof SelfSkillBlock;
  nameKr: string;
  category: 'combat' | 'practice' | 'life' | 'common';
  facilityOrTool: string;
  description: string;
  statGains: Array<keyof StatBlock>;
  icon: string;
}

export const SELF_SKILL_METAS: SelfSkillMeta[] = [
  // a. 전투스킬 (3종)
  {
    key: 'slash',
    nameKr: '슬래시 (Slash)',
    category: 'combat',
    facilityOrTool: '검류 · 도끼류 무기 실전 사용',
    description: '1%당 검/도끼 데미지 +0.5%, SP소모 -0.4% (마스터 시 딜 +50%, SP -40%). 우드워킹과 상호 시너지.',
    statGains: ['str', 'qui'],
    icon: '⚔️',
  },
  {
    key: 'thrust',
    nameKr: '쓰러스트 (Thrust)',
    category: 'combat',
    facilityOrTool: '활 · 창 · 단검류 무기 실전 사용',
    description: '1%당 활/창/단검 데미지 +0.5%, SP소모 -0.4% (마스터 시 딜 +50%, SP -40%). 레더워킹과 상호 시너지.',
    statGains: ['dex', 'qui'],
    icon: '🏹',
  },
  {
    key: 'crush',
    nameKr: '크러시 (Crush)',
    category: 'combat',
    facilityOrTool: '둔기 · 지팡이 · 권갑 실전 사용',
    description: '1%당 둔기/지팡이/주먹 데미지 +0.5%, SP소모 -0.4% (마스터 시 딜 +50%, SP -40%). 메탈워킹과 상호 시너지.',
    statGains: ['str', 'con'],
    icon: '🔨',
  },
  // b. 연습스킬 (4종)
  {
    key: 'experiment',
    nameKr: '실험 (Experiment)',
    category: 'practice',
    facilityOrTool: '📚 왕립 아카데미 / 연금 실험대',
    description: '마법사의 마법 데미지 증가 (1%당 +0.6%, 마스터 시 +60%). 수련 시 지능·속도 상승.',
    statGains: ['int', 'qui'],
    icon: '⚗️',
  },
  {
    key: 'prayer',
    nameKr: '기도 (Prayer)',
    category: 'practice',
    facilityOrTool: '⛪ 대성당 / 신전 제단',
    description: '클레릭·수도승·팔라딘의 힐량(+0.5%/%) 및 버프효율(+0.25%/%) 증가. 수련 시 신앙·체력 상승.',
    statGains: ['pie', 'con'],
    icon: '🙏',
  },
  {
    key: 'training',
    nameKr: '훈련 (Training)',
    category: 'practice',
    facilityOrTool: '⚔️ 훈련소 허수아비 / 연병장',
    description: '근접 및 원거리 무기 사용 캐릭터의 물리 데미지 증가 (1%당 +0.5%, 마스터 시 +50%). 힘·민첩·속도 상승.',
    statGains: ['str', 'dex', 'qui'],
    icon: '🎯',
  },
  {
    key: 'performance',
    nameKr: '연주 (Performance)',
    category: 'practice',
    facilityOrTool: '🎵 아카데미 음악실 / 여관 무대',
    description: '바드의 노래 반경(+0.04타일/%) 및 메즈 성공률(+0.2%/%) 증가. 수련 시 매력·속도 상승.',
    statGains: ['cha', 'qui'],
    icon: '🎼',
  },
  // c. 생활스킬 (8종)
  {
    key: 'woodworking',
    nameKr: '우드워킹 (Woodworking)',
    category: 'life',
    facilityOrTool: '🌲 벌목 도끼 & 목공 작업대',
    description: '나무를 채집하여 활대, 칼자루, 지팡이 몸통, 가구를 가공. (힘, 체력, 속도 상승)',
    statGains: ['str', 'con', 'qui'],
    icon: '🪵',
  },
  {
    key: 'metalworking',
    nameKr: '메탈워킹 (Metalworking)',
    category: 'life',
    facilityOrTool: '🪨 곡괭이 & 대장간 용광로/모루',
    description: '석재 및 광물을 채집하여 칼날, 둔기 헤드, 강철 판금, 징/버클 가공. (힘, 체력, 속도 상승)',
    statGains: ['str', 'con', 'qui'],
    icon: '⚒️',
  },
  {
    key: 'leatherworking',
    nameKr: '레더워킹 (Leatherworking)',
    category: 'life',
    facilityOrTool: '🦌 무두질 칼 & 가죽 세공대',
    description: '동물에게서 가죽을 얻어 칼집, 가죽 그립, 가죽 갑옷을 가공. (힘, 체력, 민첩 상승)',
    statGains: ['str', 'con', 'dex'],
    icon: '🧥',
  },
  {
    key: 'tailoring',
    nameKr: '재봉/직조 (Tailoring)',
    category: 'life',
    facilityOrTool: '🧵 베틀 & 재봉 바늘',
    description: '누에/식물에서 실과 직물을 얻어 마법 로브, 천옷, 갑옷 누비 내피, 활시위 제작. (민첩, 지능, 매력 상승)',
    statGains: ['dex', 'int', 'cha'],
    icon: '🪡',
  },
  {
    key: 'alchemy',
    nameKr: '알케미 (Alchemy)',
    category: 'life',
    facilityOrTool: '🌿 약초 채집낫 & 연금 증류기',
    description: '약초들을 채집하여 회복/비약, 마력 코팅제, 인챈트 촉매를 가공. (민첩, 체력, 지능 상승)',
    statGains: ['dex', 'con', 'int'],
    icon: '🧪',
  },
  {
    key: 'farming',
    nameKr: '농사 (Farming)',
    category: 'life',
    facilityOrTool: '🌾 괭이 & 농경지 밭',
    description: '농작물을 재배하여 곡물, 밀가루, 채소, 약재 원료를 수확. (힘, 체력, 신앙 상승)',
    statGains: ['str', 'con', 'pie'],
    icon: '🌾',
  },
  {
    key: 'husbandry',
    nameKr: '가축기르기 (Husbandry)',
    category: 'life',
    facilityOrTool: '🐄 목장 울타리 & 사료통',
    description: '가축을 길들이고 길러 고기, 우유, 상급 통가죽, 양모를 획득. (힘, 체력, 매력 상승)',
    statGains: ['str', 'con', 'cha'],
    icon: '🐄',
  },
  {
    key: 'cooking',
    nameKr: '요리 (Cooking)',
    category: 'life',
    facilityOrTool: '🔥 모닥불 & 주방 화덕',
    description: '농축산물을 조리하여 파티원 버프 만찬 및 전투 식량을 제작. (체력, 지능, 신앙, 매력 상승)',
    statGains: ['con', 'int', 'pie', 'cha'],
    icon: '🍲',
  },
  // d. 공통스킬 (4종)
  {
    key: 'rhetoric',
    nameKr: '화술 (Rhetoric)',
    category: 'common',
    facilityOrTool: '💬 마을 주민 대화 & 상점 흥정',
    description: '대화나 흥정을 하면 증가. 1%당 상점 할인/할증 +0.15% (마스터 시 15% 할인). (매력 상승)',
    statGains: ['cha'],
    icon: '🗣️',
  },
  {
    key: 'busking',
    nameKr: '공연 (Busking)',
    category: 'common',
    facilityOrTool: '🎪 마을 분수대 광장 & 여관 홀',
    description: '사람들 앞에서 공연하면 증가. 관객 NPC로부터 팁(골드)과 마을 명성 획득. (매력, 신앙 상승)',
    statGains: ['cha', 'pie'],
    icon: '🎭',
  },
  {
    key: 'begging',
    nameKr: '구걸 (Begging)',
    category: 'common',
    facilityOrTool: '🥣 거리 NPC에게 동정심 호소',
    description: '구걸 성공 시 소전 획득 및 10% 확률로 NPC들의 비밀 소문/던전 보물 정보 획득! (체력, 신앙 상승)',
    statGains: ['con', 'pie'],
    icon: '🪙',
  },
  {
    key: 'stealing',
    nameKr: '훔치기 (Stealing)',
    category: 'common',
    facilityOrTool: '🧤 NPC 주머니 소매치기',
    description: '성공 시 돈과 귀중품 획득, 실패 시 경비대 수배 또는 감옥 수감! (민첩, 속도 상승)',
    statGains: ['dex', 'qui'],
    icon: '🗝️',
  },
];

/**
 * Class Spec Point Multipliers (DAoC Authentic)
 */
export const CLASS_SPEC_MULTIPLIERS: Record<EntityRole, number> = {
  tank: 2.0,         // 팔라딘 (2.0x)
  ranged_dps: 2.0,   // 레인저 (2.0x)
  caster_dps: 1.5,   // 마법사 (1.5x)
  healer: 1.5,       // 클레릭 (1.5x)
  buffer: 2.0,       // 바드 (2.0x)
  monk_hybrid: 2.2,  // 수도승 (2.2x - 8 skills)
  monster: 1.0,
  npc: 1.0,
  resource_node: 0,
  admin: 3.0,
};

/**
 * Calculate DAoC 30 Bonus Point cost for a single stat increase (+0 ~ +18)
 * +1..+10: 1 pt each (10 pts)
 * +11..+15: 2 pts each (10 pts -> total 20 pts)
 * +16..+20: 3 pts each
 */
export function getBonusStatPointCost(addedAmount: number): number {
  let cost = 0;
  for (let i = 1; i <= addedAmount; i++) {
    if (i <= 10) cost += 1;
    else if (i <= 15) cost += 2;
    else cost += 3;
  }
  return cost;
}

export function getTotalBonusPointsUsed(bonus: StatBlock): number {
  return (
    getBonusStatPointCost(bonus.str) +
    getBonusStatPointCost(bonus.con) +
    getBonusStatPointCost(bonus.dex) +
    getBonusStatPointCost(bonus.qui) +
    getBonusStatPointCost(bonus.int) +
    getBonusStatPointCost(bonus.pie) +
    getBonusStatPointCost(bonus.cha)
  );
}

/**
 * Calculate total Spec Points earned at character level L (1 ~ 50)
 * Sum of level i * multiplier for i = 2..L (plus initial 5 points at level 1)
 */
export function calcTotalSpecPointsForLevel(role: EntityRole, level: number): number {
  const mult = CLASS_SPEC_MULTIPLIERS[role] || 1.5;
  const clamped = Math.max(1, Math.min(50, Math.floor(level)));
  const triangularSum = (clamped * (clamped + 1)) / 2;
  return Math.floor(triangularSum * mult);
}

/**
 * Cost to raise a Spec Skill from level 1 to targetLevel (1 ~ 50)
 * Raising from L-1 to L costs L points.
 */
export function calcCumulativeSkillCost(targetLevel: number): number {
  if (targetLevel <= 1) return 0;
  return (targetLevel * (targetLevel + 1)) / 2 - 1;
}

/**
 * Initialize default stats, race, self-skills, and spec skills for a party member
 */
export function initCharacterSheet(
  entity: EntityState,
  defaultRace: RaceId,
  defaultBonus: StatBlock,
  initialLevel = 15
): void {
  const raceDef = TERRA_ANIMA_RACES[defaultRace];
  entity.race = defaultRace;
  entity.level = initialLevel;
  if (entity.exp === undefined) {
    entity.exp = 0;
  }
  entity.maxExp = Math.max(100, Math.floor(initialLevel * 1000));
  entity.allocatedBonusStats = { ...defaultBonus };
  if (!entity.appearance) {
    entity.appearance = getDefaultAppearanceForRole(entity.role);
  }
  if (entity.isMounted === undefined) {
    entity.isMounted = false;
  }
  if (!entity.horseColor) {
    const horsePalette: Record<string, string> = {
      tank: '#f8fafc',        // 백마 (Royal White Warhorse)
      caster_dps: '#1e293b',  // 흑마 (Midnight Arcane Steed)
      ranged_dps: '#92400e',  // 적토마 (Chestnut Forest Steed)
      healer: '#fef3c7',      // 크림색 성마 (Ivory Blessed Steed)
      buffer: '#b45309',      // 황금 갈기마 (Golden Mane Steed)
      monk_hybrid: '#78350f', // 갈색 준마 (Brown Mountain Steed)
    };
    entity.horseColor = horsePalette[entity.role] || '#92400e';
  }
  if (entity.gold === undefined) {
    entity.gold = entity.category === 'player'
      ? Currency.toCopper({ gold: 100 }) // 신규 플레이어 시작 자금 (물약 1병 상당)
      : Currency.toCopper({ silver: 50 }); // 일반 NPC
  }
  if (entity.fame === undefined) {
    entity.fame = entity.category === 'player' ? 180 : 120;
  }
  if (entity.theftCount === undefined) {
    entity.theftCount = 0;
  }
  initEntityInventory20(entity);

  entity.baseStats = {
    str: raceDef.baseStats.str + defaultBonus.str,
    con: raceDef.baseStats.con + defaultBonus.con,
    dex: raceDef.baseStats.dex + defaultBonus.dex,
    qui: raceDef.baseStats.qui + defaultBonus.qui,
    int: raceDef.baseStats.int + defaultBonus.int,
    pie: raceDef.baseStats.pie + defaultBonus.pie,
    cha: raceDef.baseStats.cha + defaultBonus.cha,
  };

  entity.selfSkills = {
    slash: entity.weaponType === 'slash' ? 35.0 : 10.0,
    thrust: entity.weaponType === 'thrust' ? 35.0 : 10.0,
    crush: entity.weaponType === 'crush' ? 35.0 : 10.0,
    experiment: entity.role === 'caster_dps' ? 40.0 : 5.0,
    prayer: entity.role === 'healer' || entity.role === 'monk_hybrid' || entity.role === 'tank' ? 35.0 : 5.0,
    training: entity.role === 'tank' || entity.role === 'ranged_dps' || entity.role === 'monk_hybrid' ? 35.0 : 10.0,
    performance: entity.role === 'buffer' ? 40.0 : 5.0,
    woodworking: 25.0,
    metalworking: 25.0,
    leatherworking: 25.0,
    tailoring: 20.0,
    alchemy: 20.0,
    farming: 15.0,
    husbandry: 15.0,
    cooking: 20.0,
    rhetoric: 20.0,
    busking: entity.role === 'buffer' ? 30.0 : 10.0,
    begging: 10.0,
    stealing: entity.role === 'ranged_dps' ? 20.0 : 5.0,
  };

  // Boost racial affinities
  for (const aff of raceDef.lifeAffinities) {
    entity.selfSkills[aff] = Math.min(100, (entity.selfSkills[aff] || 0) + 15.0);
  }

  const maxSlots = entity.role === 'monk_hybrid' ? 8 : 7;
  entity.specSkillLevels = {};
  entity.selectedCastLevels = {};
  entity.equipSkillBonuses = {};

  for (let s = 1; s <= maxSlots; s++) {
    entity.specSkillLevels[s] = 5;
    entity.selectedCastLevels[s] = 5;
    entity.equipSkillBonuses[s] = 0;
  }

  entity.equipment = {
    weapon: null,
    offhand: null,
    armor: null,
  };

  recalculateEntityStats(entity, true);
}

/**
 * Recalculate Effective Stats, Max HP/SP/MP/Concentration, and Equipment +Skill Bonuses (+10 Cap)
 * Supports DAoC Base Single Buffs (Monk) + Spec Composite Buffs (Cleric) stacking!
 */
export function recalculateEntityStats(entity: EntityState, refillResources = false): void {
  if (!entity.baseStats) return;

  // 1. Sum DAoC Base Single Buffs (Monk) and Spec Composite Buffs (Cleric) separately so they stack!
  const baseBuffMax: StatBlock = { str: 0, con: 0, dex: 0, qui: 0, int: 0, pie: 0, cha: 0 };
  const specBuffMax: StatBlock = { str: 0, con: 0, dex: 0, qui: 0, int: 0, pie: 0, cha: 0 };

  for (const buff of entity.buffs) {
    if (!buff.statBonuses) continue;
    const targetPool = buff.buffCategory === 'base_single' ? baseBuffMax : specBuffMax;
    const keys: Array<keyof StatBlock> = ['str', 'con', 'dex', 'qui', 'int', 'pie', 'cha'];
    for (const k of keys) {
      const val = buff.statBonuses[k] || 0;
      if (val > targetPool[k]) targetPool[k] = val;
    }
  }

  // 2. Sum Equipment Stat & Skill Bonuses (Cap +Skill at +10!)
  const equipStatBonus: StatBlock = { str: 0, con: 0, dex: 0, qui: 0, int: 0, pie: 0, cha: 0 };
  const rawEquipSkillBonus: Record<number, number> = {};
  const maxSlots = entity.role === 'monk_hybrid' ? 8 : 7;
  for (let s = 1; s <= maxSlots; s++) rawEquipSkillBonus[s] = 0;

  if (entity.equipment) {
    const items = [entity.equipment.weapon, entity.equipment.offhand, entity.equipment.armor];
    for (const item of items) {
      if (!item) continue;
      for (const enc of item.enchants) {
        if (enc.type === 'stat') {
          const k = enc.targetKey as keyof StatBlock;
          if (equipStatBonus[k] !== undefined) {
            equipStatBonus[k] += enc.value;
          }
        } else if (enc.type === 'skill') {
          const slotNum = parseInt(enc.targetKey.replace('slot_', ''), 10);
          if (!isNaN(slotNum)) {
            rawEquipSkillBonus[slotNum] = (rawEquipSkillBonus[slotNum] || 0) + enc.value;
          }
        }
      }
    }
  }

  entity.equipSkillBonuses = {};
  for (let s = 1; s <= maxSlots; s++) {
    // Enforce strict +10 cap on equipment skill bonuses
    entity.equipSkillBonuses[s] = Math.min(10, rawEquipSkillBonus[s] || 0);
  }

  // 3. Calculate Effective Stats (Cap at 350)
  entity.effectiveStats = {
    str: Math.min(350, Math.round(entity.baseStats.str + baseBuffMax.str + specBuffMax.str + equipStatBonus.str)),
    con: Math.min(350, Math.round(entity.baseStats.con + baseBuffMax.con + specBuffMax.con + equipStatBonus.con)),
    dex: Math.min(350, Math.round(entity.baseStats.dex + baseBuffMax.dex + specBuffMax.dex + equipStatBonus.dex)),
    qui: Math.min(350, Math.round(entity.baseStats.qui + baseBuffMax.qui + specBuffMax.qui + equipStatBonus.qui)),
    int: Math.min(350, Math.round(entity.baseStats.int + baseBuffMax.int + specBuffMax.int + equipStatBonus.int)),
    pie: Math.min(350, Math.round(entity.baseStats.pie + baseBuffMax.pie + specBuffMax.pie + equipStatBonus.pie)),
    cha: Math.min(350, Math.round(entity.baseStats.cha + baseBuffMax.cha + specBuffMax.cha + equipStatBonus.cha)),
  };

  const eff = entity.effectiveStats;

  // 4. Derive Resource Pools from Effective Stats:
  // CON -> MaxHP (base 200 at 50 CON, +8 per CON), MaxSP (base 100 at 50 CON, +2 per CON)
  const newMaxHp = Math.max(80, Math.round(200 + (eff.con - 50) * 8));
  const newMaxSp = Math.max(50, Math.round(100 + (eff.con - 50) * 2));

  // Primary Mental Stat for MP & Concentration Pool:
  // Wizard & Ranger -> INT
  // Cleric, Monk, Paladin -> PIE
  // Bard -> CHA
  let mentalStat = eff.int;
  if (entity.role === 'tank' || entity.role === 'healer' || entity.role === 'monk_hybrid') {
    mentalStat = eff.pie;
  } else if (entity.role === 'buffer') {
    mentalStat = eff.cha;
  }

  const newMaxMp = Math.max(50, Math.round(100 + (mentalStat - 50) * 5));
  const newMaxConc = Math.max(80, Math.round(100 + (mentalStat - 50) * 1.0));

  // Preserve reserved concentration delta
  const reservedConc = Math.max(0, entity.resources.maxConc - entity.resources.conc);

  entity.resources.maxHp = newMaxHp;
  entity.resources.maxSp = newMaxSp;
  entity.resources.maxMp = newMaxMp;
  entity.resources.maxConc = newMaxConc;
  entity.resources.conc = Math.max(0, newMaxConc - reservedConc);

  if (refillResources) {
    entity.resources.hp = newMaxHp;
    entity.resources.sp = newMaxSp;
    entity.resources.mp = newMaxMp;
  } else {
    entity.resources.hp = Math.min(entity.resources.hp, newMaxHp);
    entity.resources.sp = Math.min(entity.resources.sp, newMaxSp);
    entity.resources.mp = Math.min(entity.resources.mp, newMaxMp);
  }

  // Update Spec Points available
  const lvl = entity.level || 15;
  const totalPts = calcTotalSpecPointsForLevel(entity.role, lvl);
  let spentPts = 0;
  if (entity.specSkillLevels) {
    for (const slotKey in entity.specSkillLevels) {
      spentPts += calcCumulativeSkillCost(entity.specSkillLevels[Number(slotKey)]);
    }
  }
  entity.specPointsTotal = totalPts;
  entity.specPointsAvailable = Math.max(0, totalPts - spentPts);
}

/**
 * Get effective skill level for a slot = selectedCastLevel (Down-ranking supported) + equipSkillBonus (capped at +10)
 */
export function getEffectiveSkillLevel(entity: EntityState, slot: number): number {
  const trained = entity.specSkillLevels?.[slot] ?? 1;
  const selected = entity.selectedCastLevels?.[slot] ?? trained;
  const clampedSelected = Math.max(1, Math.min(trained, selected));
  const equipBonus = Math.min(10, entity.equipSkillBonuses?.[slot] ?? 0);
  return clampedSelected + equipBonus;
}

/**
 * 🏃 HP-Proportional Movement Speed Formula:
 * Current Speed = BaseSpeed * (0.40 + 0.60 * (HP / MaxHP))
 * - Bard's SOS buff ignores the HP injury penalty and grants +100% speed!
 * - Bard's March Song grants out-of-combat movement speed bonus!
 * - Snare / Root CC reduces or halts speed.
 */
export function getEffectiveMoveSpeed(entity: EntityState): number {
  if (entity.cc?.type === 'root' || entity.cc?.type === 'stun' || entity.cc?.type === 'mez') {
    return 0;
  }

  const baseSpeed = entity.speed || 3.8;
  const hasSos = entity.buffs.some((b) => b.ignoreHpSpeedPenalty);

  // 1. HP ratio factor (40% minimum at 0 HP -> 100% at full HP)
  const hpRatio = Math.max(0, Math.min(1, entity.resources.hp / Math.max(1, entity.resources.maxHp)));
  const hpSpeedFactor = hasSos ? 1.0 : 0.40 + 0.60 * hpRatio;

  // 2. Buff speed bonus (SOS in combat, March Song out of combat)
  let buffBonus = 0;
  for (const b of entity.buffs) {
    if (b.moveSpeedBonus && b.moveSpeedBonus > 0) {
      if (b.ignoreHpSpeedPenalty || !entity.inCombat) {
        buffBonus = Math.max(buffBonus, b.moveSpeedBonus);
      }
    }
  }

  // 3. Snare CC penalty (from Trap, Frost Wind, Leg Sweep)
  const snarePenalty = entity.cc?.type === 'snare' ? (entity.cc.snarePct || 0) : 0;

  // 4. Warhorse Mount Speed Multiplier (3.8x gallop speed when mounted!)
  const mountMultiplier = entity.isMounted ? 3.8 : 1.0;

  // 5. GM Super Speed Multiplier
  let gmSpeedMult = 1.0;
  if (entity.category === 'player' && typeof window !== 'undefined') {
    const gm = (window as any).GM || (window as any).TerraAnimaGM;
    if (gm?.cheats?.superSpeedMult) {
      gmSpeedMult = gm.cheats.superSpeedMult;
    }
  }

  return Math.max(0.8, baseSpeed * hpSpeedFactor * (1 + buffBonus) * Math.max(0.2, 1 - snarePenalty) * mountMultiplier * gmSpeedMult);
}

/**
 * Derived Combat Multipliers from 7 Stats & Self-Taught Skills
 */
export function getDerivedCombatMetrics(entity: EntityState) {
  const eff = entity.effectiveStats || { str: 60, con: 60, dex: 60, qui: 60, int: 60, pie: 60, cha: 60 };
  const self = entity.selfSkills;

  // Weapon skill proficiency (0~100%)
  const wType = entity.weaponType;
  const weaponSkillPct = self
    ? wType === 'slash'
      ? self.slash
      : wType === 'thrust'
      ? self.thrust
      : self.crush
    : 25;

  // Practice skill proficiency (0~100%)
  const trainingPct = self?.training || 0;
  const experimentPct = self?.experiment || 0;
  const prayerPct = self?.prayer || 0;
  const performancePct = self?.performance || 0;

  // Physical Damage Multiplier:
  // STR (+0.5% per pt for Slash/Crush) or DEX (+0.5% per pt for Thrust)
  // + Weapon Skill (+0.5% per %) + Training (+0.5% per %)
  const primaryPhysStat = wType === 'thrust' ? eff.dex : eff.str;
  const physDamageMult =
    (1 + (primaryPhysStat - 50) * 0.005) *
    (1 + weaponSkillPct * 0.005) *
    (1 + trainingPct * 0.005) *
    (entity.equipment?.weapon?.attackPowerMod || 1.0);

  // Magic Damage Multiplier: Experiment (+0.6% per %)
  const magicDamageMult = 1 + experimentPct * 0.006;

  // Heal & Buff Efficiency Multiplier: PIE (+0.2% per pt) + Prayer (+0.5% heal / +0.25% buff per %)
  const healMult = (1 + (eff.pie - 50) * 0.002) * (1 + prayerPct * 0.005);
  const buffPotencyMult = 1 + prayerPct * 0.0025;

  // Crit Chance:
  // Base 5% + 0.15% per STR (Slash/Crush) or DEX (Thrust) or 0.12% per INT (Magic) + Buffs
  let buffCrit = 0;
  for (const b of entity.buffs) {
    if (b.critBonus) buffCrit += b.critBonus;
  }
  const physCritChance = Math.min(0.75, 0.05 + Math.max(0, primaryPhysStat - 50) * 0.0015 + buffCrit);
  const magicCritChance = Math.min(0.75, 0.05 + Math.max(0, eff.int - 50) * 0.0012 + buffCrit);

  // Defense / Evasion (Block, Parry, Evade from DEX: base 3% + 0.12% per DEX)
  let buffEvade = 0;
  for (const b of entity.buffs) {
    if (b.evadeBonus) buffEvade += b.evadeBonus;
  }
  const blockChance = Math.min(0.50, 0.03 + eff.dex * 0.0012);
  const parryChance = Math.min(0.50, 0.03 + eff.dex * 0.0012);
  const evadeChance = Math.min(0.65, 0.03 + eff.dex * 0.0012 + buffEvade);

  // Speed (QUI): -0.4% weapon swing time & -0.4% spell cast time per QUI point (max 40% reduction from stat)
  const quiReduction = Math.min(0.40, Math.max(0, eff.qui - 40) * 0.004);
  let buffAtkSpeed = 0;
  let buffCastSpeed = 0;
  for (const b of entity.buffs) {
    if (b.attackSpeedBonus) buffAtkSpeed += b.attackSpeedBonus;
    if (b.castSpeedBonus) buffCastSpeed += b.castSpeedBonus;
  }
  const swingTimeMult = Math.max(0.35, (1 - quiReduction) * (1 - buffAtkSpeed));
  const castTimeMult = Math.max(0.35, (1 - quiReduction) * (1 - buffCastSpeed));

  // Weapon SP Cost reduction from Slash/Thrust/Crush (-0.4% per %, up to -40%) + Weapon Hilt quality
  const hiltSpMod = entity.equipment?.weapon?.speedSpMod || 0;
  const spCostMult = Math.max(0.45, 1 - weaponSkillPct * 0.004 - hiltSpMod);

  // Max Carry Weight (50kg + 2.0kg per STR)
  const maxWeightKg = Math.round(50 + eff.str * 2.0);

  return {
    physDamageMult,
    magicDamageMult,
    healMult,
    buffPotencyMult,
    physCritChance,
    magicCritChance,
    blockChance,
    parryChance,
    evadeChance,
    swingTimeMult,
    castTimeMult,
    spCostMult,
    maxWeightKg,
    songRadiusBonus: performancePct * 0.04,
  };
}

/**
 * Practice / Train a Self-Taught Skill (UO-style 0.0% ~ 100.0% gain + Diminishing Returns Stat Gain)
 */
export function practiceSelfSkill(
  entity: EntityState,
  skillKey: keyof SelfSkillBlock,
  gainAmount = 1.2
): { newSkillVal: number; statGained?: string } {
  if (!entity.selfSkills || !entity.baseStats) return { newSkillVal: 0 };

  const meta = SELF_SKILL_METAS.find((m) => m.key === skillKey);
  const oldVal = entity.selfSkills[skillKey] || 0;
  const newVal = Math.min(100.0, Number((oldVal + gainAmount).toFixed(1)));
  entity.selfSkills[skillKey] = newVal;

  // Cross-synergy: Slash <-> Woodworking, Thrust <-> Leatherworking, Crush <-> Metalworking
  if (skillKey === 'woodworking') entity.selfSkills.slash = Math.min(100, Number((entity.selfSkills.slash + 0.3).toFixed(1)));
  if (skillKey === 'leatherworking') entity.selfSkills.thrust = Math.min(100, Number((entity.selfSkills.thrust + 0.3).toFixed(1)));
  if (skillKey === 'metalworking') entity.selfSkills.crush = Math.min(100, Number((entity.selfSkills.crush + 0.3).toFixed(1)));

  let statGained: string | undefined;
  if (meta && meta.statGains.length > 0) {
    // Pick one of the linked stats; diminishing returns as stat approaches 200
    const targetStat = meta.statGains[Math.floor(Math.random() * meta.statGains.length)];
    const curStat = entity.baseStats[targetStat];
    const chance = Math.max(0.15, 1.0 - (curStat - 50) / 160);
    if (curStat < 200 && Math.random() < chance) {
      entity.baseStats[targetStat] += 1;
      const statNames: Record<keyof StatBlock, string> = {
        str: '힘(STR)',
        con: '체력(CON)',
        dex: '민첩(DEX)',
        qui: '속도(QUI)',
        int: '지능(INT)',
        pie: '신앙(PIE)',
        cha: '매력(CHA)',
      };
      statGained = `${statNames[targetStat]} +1 상승! (${entity.baseStats[targetStat]})`;
      recalculateEntityStats(entity, false);
    }
  }

  // Special Common Skill Effects (Begging secret rumors, Busking gold tips, Stealing risk)
  if (skillKey === 'begging' && Math.random() < 0.25) {
    const rumors = [
      '🤫 [구걸 비밀소문] "북쪽 보레아스 설원 빙하 밑에 고대 거인의 보물상자가 숨겨져 있대요..."',
      '🤫 [구걸 비밀소문] "솔라리움 성채 대장장이가 오늘 밤 99퀄리티 강철 방패를 벼린다는군요!"',
      '🤫 [구걸 비밀소문] "네뷸라 늪지 서쪽 포탈을 타면 루미나 성역섬의 수정탑으로 바로 갈 수 있소."',
    ];
    EventBus.emit('LOG_EMITTED', {
      id: Math.random().toString(),
      timestamp: performance.now(),
      text: rumors[Math.floor(Math.random() * rumors.length)],
      category: 'system',
    });
  } else if (skillKey === 'busking') {
    const tipCopper = Math.floor(Currency.toCopper({ copper: 50 }) + newVal * 5);
    EventBus.emit('LOG_EMITTED', {
      id: Math.random().toString(),
      timestamp: performance.now(),
      text: `🎭 [${entity.name}] 의 거리 공연에 마을 주민들이 환호하며 팁 +${Currency.format(tipCopper)}를 던져주었습니다!`,
      category: 'crafting',
    });
  } else if (skillKey === 'stealing') {
    const successRate = 0.25 + newVal * 0.007;
    if (Math.random() > successRate) {
      EventBus.emit('LOG_EMITTED', {
        id: Math.random().toString(),
        timestamp: performance.now(),
        text: `🚨 [소매치기 발각!] 경비병이 호각을 불며 달려옵니다! (수배 위험 경고)`,
        category: 'interrupted',
      });
    } else {
      const stolenCopper = Currency.toCopper({ silver: 25 });
      EventBus.emit('LOG_EMITTED', {
        id: Math.random().toString(),
        timestamp: performance.now(),
        text: `🧤 [훔치기 성공] 눈치채지 못하게 은화 주머니(${Currency.format(stolenCopper)})를 슬쩍했습니다!`,
        category: 'crafting',
      });
    }
  }

  return { newSkillVal: newVal, statGained };
}

/**
 * ⚒️ Multi-Skill Component Crafting System
 * Assemble 3 sub-components (Primary 60%, Secondary1 25%, Secondary2 15%) into a finished item!
 */
export interface MultiSkillRecipe {
  id: string;
  name: string;
  category: CraftedEquipment['category'];
  slot: CraftedEquipment['slot'];
  primaryPart: { name: string; skill: keyof SelfSkillBlock; effectDesc: string };
  secondaryPart1: { name: string; skill: keyof SelfSkillBlock; effectDesc: string };
  secondaryPart2: { name: string; skill: keyof SelfSkillBlock; effectDesc: string };
}

export const MULTI_SKILL_RECIPES: MultiSkillRecipe[] = [
  {
    id: 'recipe_paladin_shield',
    name: '솔라리움 성기사 대형 방패',
    category: 'plate_shield',
    slot: 'offhand',
    primaryPart: { name: '강철 방패 외판', skill: 'metalworking', effectDesc: '방어력/블럭율 결정 (60%)' },
    secondaryPart1: { name: '충격흡수 누비 내피', skill: 'tailoring', effectDesc: 'SP 소모 절감 & 패리 보정 (25%)' },
    secondaryPart2: { name: '가죽 손잡이 스트랩', skill: 'leatherworking', effectDesc: '내구도 & 무게 감소 결정 (15%)' },
  },
  {
    id: 'recipe_knight_greatsword',
    name: '솔라리움 기사단 장검',
    category: 'sword_axe',
    slot: 'weapon',
    primaryPart: { name: '고탄소강 칼날 (Blade)', skill: 'metalworking', effectDesc: '기본 공격력 & 치명타 결정 (60%)' },
    secondaryPart1: { name: '떡갈나무 칼자루 (Hilt)', skill: 'woodworking', effectDesc: '휘두르는 공속 & SP 절감 결정 (25%)' },
    secondaryPart2: { name: '통가죽 칼집 (Scabbard)', skill: 'leatherworking', effectDesc: '최대 내구도 & 무게 절감 결정 (15%)' },
  },
  {
    id: 'recipe_sylvana_longbow',
    name: '실바나 정령 장궁',
    category: 'bow_leather',
    slot: 'weapon',
    primaryPart: { name: '주목나무 탄성 활대', skill: 'woodworking', effectDesc: '활 공격력 & 사거리 결정 (60%)' },
    secondaryPart1: { name: '마법 명주 활시위', skill: 'tailoring', effectDesc: '장전 속도 & SP 절감 결정 (25%)' },
    secondaryPart2: { name: '가죽 그립 & 화살통', skill: 'leatherworking', effectDesc: '내구도 & 무게 절감 결정 (15%)' },
  },
  {
    id: 'recipe_lumina_archstaff',
    name: '루미나 비전 대지팡이',
    category: 'robe_cloth',
    slot: 'weapon',
    primaryPart: { name: '고목 지팡이 몸통', skill: 'woodworking', effectDesc: '기본 공격력 & 마법 증폭 결정 (60%)' },
    secondaryPart1: { name: '연금 마력 보석 코팅', skill: 'alchemy', effectDesc: '주문 시전속도 & MP 절감 결정 (25%)' },
    secondaryPart2: { name: '미스릴 하단 마운트', skill: 'metalworking', effectDesc: '내구도 & 무게 절감 결정 (15%)' },
  },
  {
    id: 'recipe_cleric_mace',
    name: '대성당 축복의 메이스',
    category: 'mace_staff',
    slot: 'weapon',
    primaryPart: { name: '은도금 타격 헤드', skill: 'metalworking', effectDesc: '크러시 공격력 & 치유증폭 결정 (60%)' },
    secondaryPart1: { name: '물푸레나무 자루', skill: 'woodworking', effectDesc: '공속/시전속도 & SP 절감 결정 (25%)' },
    secondaryPart2: { name: '양가죽 손목 그립', skill: 'leatherworking', effectDesc: '내구도 & 무게 절감 결정 (15%)' },
  },
];

export function craftMultiSkillEquipment(
  crafter: EntityState,
  recipe: MultiSkillRecipe,
  forceMasterpiece99 = false
): CraftedEquipment {
  const skills = crafter.selfSkills || ({} as SelfSkillBlock);

  const rollQuality = (skillKey: keyof SelfSkillBlock): number => {
    if (forceMasterpiece99) return Math.random() < 0.35 ? 100 : 99;
    const prof = skills[skillKey] || 30;
    const baseQ = 68 + prof * 0.28;
    const variance = (Math.random() - 0.35) * 10;
    return Math.max(60, Math.min(100, Math.round(baseQ + variance)));
  };

  const qPrimary = rollQuality(recipe.primaryPart.skill);
  const qSec1 = rollQuality(recipe.secondaryPart1.skill);
  const qSec2 = rollQuality(recipe.secondaryPart2.skill);

  // Weighted total quality (60% + 25% + 15%)
  const finalQuality = forceMasterpiece99
    ? Math.max(99, Math.round(qPrimary * 0.6 + qSec1 * 0.25 + qSec2 * 0.15))
    : Math.round(qPrimary * 0.6 + qSec1 * 0.25 + qSec2 * 0.15);

  // Practice the 3 crafting skills automatically!
  practiceSelfSkill(crafter, recipe.primaryPart.skill, 0.8);
  practiceSelfSkill(crafter, recipe.secondaryPart1.skill, 0.5);
  practiceSelfSkill(crafter, recipe.secondaryPart2.skill, 0.4);

  return {
    id: 'item_' + Math.random().toString(36).substring(2, 9),
    name: `${recipe.name} [${finalQuality}%]`,
    category: recipe.category,
    slot: recipe.slot,
    itemLevel: crafter.level || 25,
    quality: finalQuality,
    components: {
      primary: {
        id: 'p1',
        name: recipe.primaryPart.name,
        partType: 'primary',
        skillUsed: recipe.primaryPart.skill,
        quality: qPrimary,
      },
      secondary1: {
        id: 'p2',
        name: recipe.secondaryPart1.name,
        partType: 'secondary1',
        skillUsed: recipe.secondaryPart1.skill,
        quality: qSec1,
      },
      secondary2: {
        id: 'p3',
        name: recipe.secondaryPart2.name,
        partType: 'secondary2',
        skillUsed: recipe.secondaryPart2.skill,
        quality: qSec2,
      },
    },
    attackPowerMod: Number((0.6 + (qPrimary / 100) * 0.6).toFixed(2)), // up to 1.20x at 100%
    speedSpMod: Number((((qSec1 - 70) / 30) * 0.15).toFixed(2)),      // +15% SP save & speed at 100%
    durabilityMod: Number((0.7 + (qSec2 / 100) * 0.8).toFixed(2)),   // up to 150% durability
    weightKg: Number((6.0 * (1.2 - (qSec2 / 100) * 0.4)).toFixed(1)),
    enchants: [],
  };
}

/**
 * 🔮 Enchanter NPC System:
 * Rolls random class-appropriate +Skill and +Stat options based on item Quality and Level!
 */
export function enchantEquipmentByNpc(
  item: CraftedEquipment,
  targetOwner: EntityState,
  skillNamesMap: Record<number, string>
): CraftedEquipment {
  const q = item.quality;
  let slotCount = 1;
  let minVal = 1;
  let maxVal = 2;

  if (q >= 100) {
    slotCount = 4;
    minVal = 3;
    maxVal = 4;
  } else if (q >= 99) {
    slotCount = 3 + (Math.random() < 0.6 ? 1 : 0); // 3~4 slots
    minVal = 2;
    maxVal = 3;
  } else if (q >= 96) {
    slotCount = 2 + (Math.random() < 0.5 ? 1 : 0);
    minVal = 1;
    maxVal = 3;
  } else if (q >= 90) {
    slotCount = 2;
    minVal = 1;
    maxVal = 2;
  }

  // Build valid option pool for the character's class/equipment category
  const maxSkillSlot = targetOwner.role === 'monk_hybrid' ? 8 : 7;
  const candidatePool: Array<{ type: 'skill' | 'stat'; key: string; name: string }> = [];

  for (let s = 1; s <= maxSkillSlot; s++) {
    candidatePool.push({
      type: 'skill',
      key: `slot_${s}`,
      name: skillNamesMap[s] || `전문스킬 ${s}`,
    });
  }

  // Add primary stats for this role
  const statCandidates: Array<{ key: keyof StatBlock; name: string }> = [
    { key: 'str', name: '힘(STR)' },
    { key: 'con', name: '체력(CON)' },
    { key: 'dex', name: '민첩(DEX)' },
    { key: 'qui', name: '속도(QUI)' },
    { key: 'int', name: '지능(INT)' },
    { key: 'pie', name: '신앙(PIE)' },
    { key: 'cha', name: '매력(CHA)' },
  ];
  for (const st of statCandidates) {
    candidatePool.push({ type: 'stat', key: st.key, name: st.name });
  }

  // Shuffle and pick unique options (Prioritize at least 2 skill bonuses on 99%+ items!)
  const enchants: EnchantBonus[] = [];
  const usedKeys = new Set<string>();

  for (let i = 0; i < slotCount; i++) {
    const preferSkill = i < 2; // First 2 slots strongly favor +Skill (like Taunt +2, Healing Chant +2)
    const filtered = candidatePool.filter(
      (c) => !usedKeys.has(c.key) && (!preferSkill || c.type === 'skill')
    );
    const pool = filtered.length > 0 ? filtered : candidatePool.filter((c) => !usedKeys.has(c.key));
    if (pool.length === 0) break;

    const chosen = pool[Math.floor(Math.random() * pool.length)];
    usedKeys.add(chosen.key);

    const roll = minVal + Math.floor(Math.random() * (maxVal - minVal + 1));
    const value = chosen.type === 'stat' ? roll * 4 : roll; // e.g., Skill +2~+3 or Stat +8~+12

    enchants.push({
      type: chosen.type,
      targetKey: chosen.key,
      targetName: chosen.name,
      value,
    });
  }

  item.enchants = enchants;
  return item;
}

// ============================================================================
// 🎒 20-SLOT INVENTORY SYSTEM (All 6 Party Characters & All World NPCs)
// ============================================================================

export const HORSE_COAT_PALETTE: Array<{ name: string; hex: string }> = [
  { name: '왕실 백마 (White)', hex: '#f8fafc' },
  { name: '적토마 (Chestnut)', hex: '#92400e' },
  { name: '칠흑마 (Black)', hex: '#1e293b' },
  { name: '황금 갈기마 (Gold)', hex: '#b45309' },
  { name: '크림 성마 (Ivory)', hex: '#fef3c7' },
  { name: '회색 군마 (Dapple)', hex: '#64748b' },
];

/**
 * Ensures any Character or NPC has a 20-slot inventory (length === 20) populated with default items.
 */
export function initEntityInventory20(entity: EntityState): void {
  if (entity.unlockedSlots === undefined) {
    entity.unlockedSlots = entity.category === 'player' ? INVENTORY_BASE_UNLOCKED : INVENTORY_CAPACITY;
  }

  if (entity.inventory && entity.inventory.length === INVENTORY_CAPACITY) return;

  const slots: Array<InventoryItem | null> = new Array(INVENTORY_CAPACITY).fill(null);
  if (entity.inventory && Array.isArray(entity.inventory)) {
    for (let i = 0; i < entity.inventory.length && i < INVENTORY_CAPACITY; i++) {
      slots[i] = entity.inventory[i];
    }
    entity.inventory = slots;
    return;
  }

  if (entity.category === 'npc') {
    initNpcInventory20(entity, slots);
    entity.inventory = slots;
    return;
  }

  // 플레이어 및 파티원 초기 아이템 0개 (튜토리얼 NPC에게 지급받도록 함)
  entity.inventory = slots;
}

/**
 * Populates 20-slot inventory for World NPCs (Stable Master, Enchanter, Blacksmith, Alchemist, Quartermaster, Innkeeper)
 */
export function initNpcInventory20(npc: EntityState, slots: Array<InventoryItem | null>): void {
  const id = npc.id;
  let items: InventoryItem[] = [];

  // 오직 마구간지기(말 상인)만 승마표 및 말 관리 용품을 판매합니다.
  // 다른 모든 상인은 제거되어 모든 물품은 유저들이 직접 채집/제작하여 유저간 거래로 유통됩니다.
  if (id.includes('stable')) {
    // 🐎 말 상인 / 마구간지기 (20칸 인벤토리 - 각 행선지 승마표 및 말 용품)
    items = [
      { id: `${id}_t1`, name: '오크헤이븐행 승마표', icon: '🎫', category: 'quest', rarity: 'common', quantity: 20, maxStack: 50, weightKg: 0.1, valueGold: 100, routeTargetStableId: 'stable_z1_oakhaven', description: '말 상인에게 구매 후 다시 넘겨주면 파티 전원이 말을 타고 [Zone 1 오크헤이븐 마구간]으로 이동합니다. (1인당 100코퍼 / 6인 파티 600코퍼 리더 부담)' },
      { id: `${id}_t2`, name: '왕도 솔라리움행 승마표', icon: '🎫', category: 'quest', rarity: 'common', quantity: 20, maxStack: 50, weightKg: 0.1, valueGold: 100, routeTargetStableId: 'stable_z1_solarium', description: '말 상인에게 구매 후 다시 넘겨주면 파티 전원이 말을 타고 [Zone 1 솔라리움 대성문 마구간]으로 이동합니다. (1인당 100코퍼 / 6인 파티 600코퍼 리더 부담)' },
      { id: `${id}_t3`, name: '솔라리스 십자로행 승마표', icon: '🎫', category: 'quest', rarity: 'common', quantity: 20, maxStack: 50, weightKg: 0.1, valueGold: 100, routeTargetStableId: 'stable_z1_crossroads', description: '말 상인에게 구매 후 다시 넘겨주면 파티 전원이 말을 타고 [Zone 1 중앙 십자로 마구간]으로 이동합니다. (1인당 100코퍼 / 6인 파티 600코퍼 리더 부담)' },
      { id: `${id}_t4`, name: '크롬레크 평원행 승마표', icon: '🎫', category: 'quest', rarity: 'fine', quantity: 15, maxStack: 50, weightKg: 0.1, valueGold: 100, routeTargetStableId: 'stable_z2_cromlech', description: '말 상인에게 구매 후 다시 넘겨주면 파티 전원이 말을 타고 [Zone 2 크롬레크 평원 마구간]으로 이동합니다. (1인당 100코퍼 / 6인 파티 600코퍼 리더 부담)' },
      { id: `${id}_t5`, name: '실바나 대삼림행 승마표', icon: '🎫', category: 'quest', rarity: 'fine', quantity: 15, maxStack: 50, weightKg: 0.1, valueGold: 100, routeTargetStableId: 'stable_z3_sylvana', description: '말 상인에게 구매 후 다시 넘겨주면 파티 전원이 말을 타고 [Zone 3 실바나 고분 마구간]으로 이동합니다. (1인당 100코퍼 / 6인 파티 600코퍼 리더 부담)' },
      { id: `${id}_t6`, name: '노르드가드 설원행 승마표', icon: '🎫', category: 'quest', rarity: 'fine', quantity: 15, maxStack: 50, weightKg: 0.1, valueGold: 100, routeTargetStableId: 'stable_z4_nordgard', description: '말 상인에게 구매 후 다시 넘겨주면 파티 전원이 말을 타고 [Zone 4 노르드가드 설원 마구간]으로 이동합니다. (1인당 100코퍼 / 6인 파티 600코퍼 리더 부담)' },
      { id: `${id}_t7`, name: '네뷸라 늪지행 승마표', icon: '🎫', category: 'quest', rarity: 'masterwork', quantity: 12, maxStack: 50, weightKg: 0.1, valueGold: 100, routeTargetStableId: 'stable_z5_nebula', description: '말 상인에게 구매 후 다시 넘겨주면 파티 전원이 말을 타고 [Zone 5 네뷸라 늪지 마구간]으로 이동합니다. (1인당 100코퍼 / 6인 파티 600코퍼 리더 부담)' },
      { id: `${id}_t8`, name: '이그니스 황무지행 승마표', icon: '🎫', category: 'quest', rarity: 'masterwork', quantity: 12, maxStack: 50, weightKg: 0.1, valueGold: 100, routeTargetStableId: 'stable_z6_ignis', description: '말 상인에게 구매 후 다시 넘겨주면 파티 전원이 말을 타고 [Zone 6 이그니스 황무지 마구간]으로 이동합니다. (1인당 100코퍼 / 6인 파티 600코퍼 리더 부담)' },
      { id: `${id}_t9`, name: '루미나 성역행 승마표', icon: '🎫', category: 'quest', rarity: 'legendary', quantity: 10, maxStack: 50, weightKg: 0.1, valueGold: 100, routeTargetStableId: 'stable_z7_lumina', description: '말 상인에게 구매 후 다시 넘겨주면 파티 전원이 말을 타고 [Zone 7 루미나 성역 마구간]으로 이동합니다. (1인당 100코퍼 / 6인 파티 600코퍼 리더 부담)' },
      { id: `${id}_6`, name: '특등급 건초 묶음', icon: '🌾', category: 'material', rarity: 'common', quantity: 25, maxStack: 50, weightKg: 0.5, valueGold: 50, description: '군마의 스태미나를 채워주는 향긋한 건초 (50 코퍼)' },
      { id: `${id}_7`, name: '아삭한 설탕 당근', icon: '🥕', category: 'consumable', rarity: 'common', quantity: 18, maxStack: 50, weightKg: 0.2, valueGold: 60, description: '말에게 먹이면 기분이 좋아지는 당근 (60 코퍼)' },
      { id: `${id}_8`, name: '강철 편자 세트', icon: '🧲', category: 'mount_gear', rarity: 'fine', quantity: 6, maxStack: 10, weightKg: 1.4, valueGold: Currency.toCopper({ silver: 1 }), description: '조약돌 가도 주행 속도를 높여주는 강철 말굽 편자 (1 실버)' },
    ];
  } else {
    // 마구간지기 외 일반 NPC는 상점 판매 아이템이 없습니다.
    items = [];
  }

  for (let i = 0; i < items.length && i < INVENTORY_CAPACITY; i++) {
    slots[i] = items[i];
  }
}

/**
 * Adds an item into an entity's 20-slot inventory (stacks if possible, otherwise first empty slot 0..19).
 */
export function addItemToInventory(entity: EntityState, newItem: InventoryItem): boolean {
  initEntityInventory20(entity);
  const inv = entity.inventory!;

  // 1. Try stacking with existing item of same name & category
  if (newItem.maxStack > 1) {
    for (let i = 0; i < INVENTORY_CAPACITY; i++) {
      const slot = inv[i];
      if (slot && slot.name === newItem.name && slot.category === newItem.category && slot.quantity < slot.maxStack) {
        const space = slot.maxStack - slot.quantity;
        const addAmt = Math.min(space, newItem.quantity);
        slot.quantity += addAmt;
        newItem.quantity -= addAmt;
        if (newItem.quantity <= 0) return true;
      }
    }
  }

  // 2. Place remaining in first empty unlocked slot (0..unlockedSlots - 1)
  const maxSlots = Math.min(INVENTORY_CAPACITY, entity.unlockedSlots ?? INVENTORY_CAPACITY);
  for (let i = 0; i < maxSlots; i++) {
    if (!inv[i]) {
      inv[i] = { ...newItem };
      return true;
    }
  }

  return false; // All unlocked slots are full
}

/**
 * Transfers an item at `slotIndex` from `fromEntity` (20 slots) to `toEntity` (20 slots).
 */
export function transferInventorySlot(
  fromEntity: EntityState,
  slotIndex: number,
  toEntity: EntityState
): { ok: boolean; msg: string } {
  initEntityInventory20(fromEntity);
  initEntityInventory20(toEntity);

  if (slotIndex < 0 || slotIndex >= INVENTORY_CAPACITY) {
    return { ok: false, msg: '유효하지 않은 인벤토리 슬롯입니다.' };
  }
  const item = fromEntity.inventory![slotIndex];
  if (!item) {
    return { ok: false, msg: '빈 슬롯입니다.' };
  }

  // Clone item to transfer (if stack > 1, transfer 1 unit or full stack; let's transfer 1 unit if consumable/material > 1, or full item)
  const transferQty = item.quantity > 1 ? 1 : 1;
  const copyToMove: InventoryItem = {
    ...item,
    id: item.id + '_' + Math.random().toString(36).substring(2, 6),
    quantity: transferQty,
  };

  const added = addItemToInventory(toEntity, copyToMove);
  if (!added) {
    return { ok: false, msg: `⚠️ [${toEntity.name}]의 20칸 인벤토리가 가득 찼습니다!` };
  }

  item.quantity -= transferQty;
  if (item.quantity <= 0) {
    fromEntity.inventory![slotIndex] = null;
  }

  return {
    ok: true,
    msg: `🎒 [${fromEntity.name}] ➔ [${toEntity.name}] 인벤토리로 [${item.icon} ${item.name}] ×${transferQty} 이동 완료!`,
  };
}

/**
 * Compacts non-null items to the front of the 20-slot array while keeping length === 20.
 */
export function sortInventory20(entity: EntityState): void {
  initEntityInventory20(entity);
  const nonNull = entity.inventory!.filter((it): it is InventoryItem => it !== null);
  const sorted: Array<InventoryItem | null> = new Array(INVENTORY_CAPACITY).fill(null);
  for (let i = 0; i < nonNull.length && i < INVENTORY_CAPACITY; i++) {
    sorted[i] = nonNull[i];
  }
  entity.inventory = sorted;
}

/**
 * Calculates total weight (kg) carried in the 20-slot inventory + equipped items.
 */
export function getInventoryTotalWeight(entity: EntityState): number {
  initEntityInventory20(entity);
  let total = 0;
  for (const it of entity.inventory!) {
    if (it) {
      total += it.weightKg * it.quantity;
    }
  }
  if (entity.equipment?.weapon) total += entity.equipment.weapon.weightKg;
  if (entity.equipment?.offhand) total += entity.equipment.offhand.weightKg;
  if (entity.equipment?.armor) total += entity.equipment.armor.weightKg;
  return Number(total.toFixed(1));
}

/**
 * Returns Fame (명성 수치) rank title, badge, color, and next tier threshold.
 * Earned via Quest rewards, Elite kills, and Boss hunts; penalized by Stealing (도둑질).
 */
export function getFameRankInfo(fame: number): {
  rankTitle: string;
  badge: string;
  color: string;
  nextTier: number;
  desc: string;
} {
  if (fame < 0) {
    return {
      rankTitle: '지명수배 도적 (Wanted Outlaw)',
      badge: '🚨',
      color: '#ef4444',
      nextTier: 0,
      desc: 'NPC 또는 파티 동료의 가방에 손을 대는 도둑질로 인해 왕국 내 명성이 추락한 상태입니다.',
    };
  }
  if (fame < 100) {
    return {
      rankTitle: '무명 모험가 (Novice Adventurer)',
      badge: '🌱',
      color: '#94a3b8',
      nextTier: 100,
      desc: '솔라리스 언덕에 막 이름을 알리기 시작한 초보 모험가입니다.',
    };
  }
  if (fame < 300) {
    return {
      rankTitle: '솔라리스의 신예 (Rising Star)',
      badge: '🛡️',
      color: '#38bdf8',
      nextTier: 300,
      desc: '퀘스트 완수와 마수 토벌로 오크헤이븐과 십자로 일대에서 신망을 얻은 모험가입니다.',
    };
  }
  if (fame < 600) {
    return {
      rankTitle: '왕국의 이름난 영웅 (Renowned Hero)',
      badge: '⚔️',
      color: '#a855f7',
      nextTier: 600,
      desc: '엘리트 마수와 드래곤 보스 토벌에 앞장서 왕도 솔라리움에서도 칭송받는 영웅입니다.',
    };
  }
  if (fame < 1000) {
    return {
      rankTitle: '대륙의 대학살자·챔피언 (Realm Champion)',
      badge: '👑',
      color: '#f59e0b',
      nextTier: 1000,
      desc: '7대 존의 월드 보스들을 제압하며 대륙 전역에 위명을 떨친 챔피언입니다.',
    };
  }
  return {
    rankTitle: '테라 아니마의 전설 (Living Legend)',
    badge: '🌟',
    color: '#fde047',
    nextTier: 2000,
    desc: '역사에 영원히 기록될 불멸의 명성을 성취한 전설적인 영웅입니다.',
  };
}

/**
 * Adjusts an entity's Fame (명성 수치) and logs the change.
 */
export function adjustEntityFame(entity: EntityState, delta: number, reason: string): number {
  const prev = entity.fame ?? 120;
  const next = prev + delta;
  entity.fame = next;
  const rank = getFameRankInfo(next);
  const sign = delta >= 0 ? `+${delta}` : `${delta}`;
  EventBus.emit('LOG_EMITTED', {
    id: Math.random().toString(),
    timestamp: performance.now(),
    text: `${delta >= 0 ? '🏆' : '🚨'} [명성 변동] [${entity.name}] 명성 ${sign} (${reason}) ➔ 현재 명성: ${next} pt [${rank.badge} ${rank.rankTitle}]`,
    category: 'system',
  });
  return next;
}

/**
 * Legitimate purchase from an NPC's 20-slot inventory.
 * If the item is a Horse Route Ticket (`routeTargetStableId`), the Party Leader pays for ALL `partySize` (6) members!
 */
export function buyItemFromNpcInventory(
  buyerLeader: EntityState,
  npc: EntityState,
  slotIndex: number,
  partySize = 6
): { ok: boolean; msg: string } {
  initEntityInventory20(buyerLeader);
  initEntityInventory20(npc);

  const item = npc.inventory![slotIndex];
  if (!item) {
    return { ok: false, msg: '빈 슬롯입니다.' };
  }

  const isHorseTicket = Boolean(item.routeTargetStableId || item.name.includes('승마표') || item.name.includes('마패'));
  const totalCost = isHorseTicket ? item.valueGold * partySize : item.valueGold;
  const currentGold = buyerLeader.gold ?? Currency.toCopper({ gold: 100 });

  if (currentGold < totalCost) {
    return {
      ok: false,
      msg: `⚠️ 파티 리더 [${buyerLeader.name}]의 소지금(${Currency.format(currentGold)})이 부족합니다! (필요 금액: ${Currency.format(totalCost)})`,
    };
  }

  const purchasedCopy: InventoryItem = {
    ...item,
    id: `${item.id}_bought_${Math.random().toString(36).substring(2, 6)}`,
    quantity: 1,
    isStolen: false,
  };

  const added = addItemToInventory(buyerLeader, purchasedCopy);
  if (!added) {
    return { ok: false, msg: `⚠️ [${buyerLeader.name}]의 인벤토리(${buyerLeader.unlockedSlots || 20}칸)가 가득 찼습니다! 다이아로 슬롯을 확장하세요.` };
  }

  // Deduct copper from Party Leader
  buyerLeader.gold = currentGold - totalCost;

  // Reduce NPC stock by 1
  item.quantity -= 1;
  if (item.quantity <= 0) {
    npc.inventory![slotIndex] = null;
  }

  const msg = isHorseTicket
    ? `💰 [승마표 정식 구매] 파티 리더 [${buyerLeader.name}]님이 ${partySize}인 파티 전원분 말 값 ${Currency.format(totalCost)} (1인당 ${Currency.format(item.valueGold)} × ${partySize}명)를 전액 부담하여 [${item.icon} ${item.name}]을(를) 구매했습니다! (이제 내 인벤토리에서 [🎫 말 상인에게 표 넘겨주기]를 누르면 전원 말을 타고 출발합니다!)`
    : `💰 [상점 구매] [${buyerLeader.name}]님이 ${Currency.format(totalCost)}를 지불하고 [${npc.name}]에게서 [${item.icon} ${item.name}] ×1을(를) 정식 구매했습니다.`;

  EventBus.emit('LOG_EMITTED', {
    id: Math.random().toString(),
    timestamp: performance.now(),
    text: msg,
    category: 'system',
  });

  return { ok: true, msg };
}

/**
 * Stealing / Pickpocketing (도둑질):
 * Taking an item directly out of an NPC's inventory OR another Party Member's bag without paying is treated as STEALING (도둑질)!
 */
export function stealFromInventorySlot(
  thief: EntityState,
  victim: EntityState,
  slotIndex: number
): { ok: boolean; caught: boolean; msg: string } {
  initEntityInventory20(thief);
  initEntityInventory20(victim);

  const item = victim.inventory![slotIndex];
  if (!item) {
    return { ok: false, caught: false, msg: '빈 슬롯입니다.' };
  }

  // Calculate steal chance from DEX + stealing life skill (0~100%)
  const dex = thief.effectiveStats?.dex || thief.baseStats?.dex || 60;
  const stealSkill = thief.selfSkills?.stealing || 10;
  const successChance = Math.min(0.88, Math.max(0.35, 0.35 + stealSkill * 0.004 + (dex - 50) * 0.003));
  const roll = Math.random();

  // Practice Stealing life skill
  practiceSelfSkill(thief, 'stealing', 0.6);
  thief.theftCount = (thief.theftCount ?? 0) + 1;

  // Shift morality toward Evil (악행: 도둑질)
  if (thief.personality && thief.personality.axes) {
    thief.personality.axes.moral = Math.max(-100, thief.personality.axes.moral - 15);
  }

  const isCompanionVictim = victim.category === 'companion' || victim.category === 'player';
  const targetTypeLabel = isCompanionVictim ? '내 파티 동료 가방' : 'NPC 인벤토리';

  if (roll <= successChance) {
    // Steal succeeded! Move 1 unit into thief's inventory marked as [훔친 장물]
    const stolenCopy: InventoryItem = {
      ...item,
      id: `${item.id}_stolen_${Math.random().toString(36).substring(2, 6)}`,
      name: item.name.includes('[장물]') ? item.name : `${item.name} [장물]`,
      quantity: 1,
      isStolen: true,
    };

    const added = addItemToInventory(thief, stolenCopy);
    if (!added) {
      return { ok: false, caught: false, msg: `⚠️ [${thief.name}]의 20칸 가방이 가득 차서 훔칠 수 없습니다!` };
    }

    item.quantity -= 1;
    if (item.quantity <= 0) {
      victim.inventory![slotIndex] = null;
    }

    adjustEntityFame(thief, -25, `${targetTypeLabel}(${victim.name}) 도둑질 감행`);

    const msg = isCompanionVictim
      ? `🕵️ [동료 가방 도둑질!] [${thief.name}]님이 파티원 [${victim.name}]의 가방에 몰래 손을 대어 [${item.icon} ${item.name}] ×1을(를) 훔쳤습니다! (도둑질 숙련 상승 / 선악 -15 / 명성 -25) — 🗣️ ${victim.name}: "잠깐, 내 가방이 왜 열려 있지?!"`
      : `🕵️ [NPC 인벤토리 도둑질!] [${thief.name}]님이 [${victim.name}]의 인벤토리에서 돈을 내지 않고 [${item.icon} ${item.name}] ×1을(를) 몰래 훔쳤습니다! (도둑질 숙련 상승 / 선악 -15 / 명성 -25)`;

    EventBus.emit('LOG_EMITTED', {
      id: Math.random().toString(),
      timestamp: performance.now(),
      text: msg,
      category: 'system',
    });

    return { ok: true, caught: false, msg };
  } else {
    // Caught red-handed!
    adjustEntityFame(thief, -50, `${targetTypeLabel}(${victim.name}) 도둑질 현장 적발!`);

    const msg = isCompanionVictim
      ? `🚨 [동료 가방 도둑질 적발!] [${thief.name}]님이 파티원 [${victim.name}]의 가방에 손을 대다가 딱 걸렸습니다! 🗣️ ${victim.name}: "이봐! 동료 가방에 손을 대다니 제정신이야?!" (명성 -50 / 선악 -15)`
      : `🚨 [NPC 도둑질 현장 적발!] [${thief.name}]님이 [${victim.name}]의 인벤토리에서 [${item.icon} ${item.name}]을(를) 몰래 꺼내려다 현장에서 적발되었습니다! 🗣️ ${victim.name}: "도둑이야! 경비병!!" (명성 -50 / 선악 -15)`;

    EventBus.emit('LOG_EMITTED', {
      id: Math.random().toString(),
      timestamp: performance.now(),
      text: msg,
      category: 'interrupted',
    });

    return { ok: false, caught: true, msg };
  }
}

/**
 * Checks if a dropped item matches a character's profession/role
 */
export function isItemMatchingRole(role: EntityRole, item: InventoryItem): boolean {
  const cat = item.category;
  const name = item.name.toLowerCase();
  const desc = item.description.toLowerCase();

  // If item is crafted equipment with category
  if (item.craftedEquip) {
    const eqCat = item.craftedEquip.category;
    switch (role) {
      case 'tank': // 팔라딘
        return eqCat === 'plate_shield' || eqCat === 'sword_axe';
      case 'ranged_dps': // 레인저
        return eqCat === 'bow_leather';
      case 'caster_dps': // 위저드
        return eqCat === 'robe_cloth';
      case 'healer': // 클레릭
        return eqCat === 'mace_staff';
      case 'buffer': // 바드
        return eqCat === 'bow_leather' || eqCat === 'robe_cloth';
      case 'monk_hybrid': // 수도승
        return eqCat === 'mace_staff' || eqCat === 'robe_cloth';
      default:
        return true;
    }
  }

  // Weapons and offhand checks
  if (cat === 'weapon' || cat === 'offhand' || cat === 'armor') {
    switch (role) {
      case 'tank': // 팔라딘
        return name.includes('검') || name.includes('방패') || name.includes('판금') || name.includes('갑옷') || desc.includes('팔라딘') || desc.includes('기사');
      case 'ranged_dps': // 레인저
        return name.includes('활') || name.includes('가죽') || name.includes('단검') || desc.includes('레인저') || desc.includes('궁수');
      case 'caster_dps': // 위저드
        return name.includes('지팡이') || name.includes('로브') || name.includes('마법') || desc.includes('위저드') || desc.includes('마법사');
      case 'healer': // 클레릭
        return name.includes('메이스') || name.includes('둔기') || name.includes('성서') || name.includes('치유') || desc.includes('클레릭') || desc.includes('사제');
      case 'buffer': // 바드
        return name.includes('악기') || name.includes('하프') || name.includes('류트') || name.includes('경갑') || desc.includes('바드');
      case 'monk_hybrid': // 수도승
        return name.includes('권갑') || name.includes('봉') || name.includes('도복') || desc.includes('수도승') || desc.includes('수도사');
      default:
        return true;
    }
  }

  // Consumables: Mana vs Healing
  if (cat === 'consumable') {
    if (name.includes('마나') || name.includes('mp')) {
      return role === 'caster_dps' || role === 'healer' || role === 'buffer' || role === 'monk_hybrid';
    }
    if (name.includes('기력') || name.includes('sp')) {
      return role === 'tank' || role === 'ranged_dps' || role === 'monk_hybrid';
    }
    return true;
  }

  return false;
}

/**
 * Distributes an item among party members:
 * 1st priority: Matching class / role
 * 2nd priority: Highest aggro / threat accumulated in combat
 * If no threat or ties: first valid party member with bag space
 */
export function distributeLootToParty(
  party: EntityState[],
  item: InventoryItem,
  threatMap: Record<string, number>
): { target: EntityState; reason: string } | null {
  if (party.length === 0) return null;

  // 1st priority: Filter party members whose role matches the item
  const matchingMembers = party.filter((m) => isItemMatchingRole(m.role, item));

  const candidatePool = matchingMembers.length > 0 ? matchingMembers : party;

  // 2nd priority: Sort by highest threat / aggro
  const sorted = [...candidatePool].sort((a, b) => {
    const threatA = threatMap[a.id] || 0;
    const threatB = threatMap[b.id] || 0;
    return threatB - threatA;
  });

  // Try to find one with available inventory space
  for (const candidate of sorted) {
    const cloned = { ...item };
    const added = addItemToInventory(candidate, cloned);
    if (added) {
      const reason = matchingMembers.includes(candidate)
        ? `[직업 적합 1순위: ${candidate.job || candidate.role}]`
        : `[어그로 2순위: 위협도 ${threatMap[candidate.id] || 0}]`;
      return { target: candidate, reason };
    }
  }

  // If candidate pool was full, try anyone else in the party
  for (const candidate of party) {
    if (!sorted.includes(candidate)) {
      const cloned = { ...item };
      const added = addItemToInventory(candidate, cloned);
      if (added) {
        return { target: candidate, reason: '[파티원 잔여 가방 공간 분배]' };
      }
    }
  }

  return null;
}

